import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, KeyRound, MapPin, CarFront, Clock, CreditCard, ThumbsUp, CalendarDays, Headphones } from "lucide-react";
import SearchBar from "../components/SearchBar";
import VehicleCard from "../components/VehicleCard";
import api from "../lib/api";

export default function Landing() {
  const [featured, setFeatured] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    api.get("/vehicles", { params: { limit: 8, sort: "rating" } }).then((r) => setFeatured(r.data || [])).catch(() => {});
    api.get("/vehicles/cities").then((r) => setCities(r.data || [])).catch(() => {});
  }, []);

  return (
    <div className="relative">
      {/* HERO */}
      <section className="relative" data-testid="hero-section">
        <div className="relative overflow-hidden min-h-[440px] md:min-h-[520px] lg:min-h-[700px]">
          <div className="absolute inset-0 lg:left-[28%]">
            <img
              src="https://images.pexels.com/photos/3779745/pexels-photo-3779745.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1600"
              alt="Woman waving from a car on a coastal road"
              className="hero-img w-full h-full object-cover"
            />
          </div>
          <div className="hero-fade absolute inset-0" />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-white via-white/60 to-transparent" />
          <p className="font-script hidden lg:block absolute right-[6%] top-14 text-2xl leading-[1.2] text-[#5ea3ff] -rotate-6 whitespace-pre-line text-right select-none uppercase [text-shadow:0_2px_12px_rgba(255,255,255,0.9)]" data-testid="hero-script">
            {"More\nPlaces\nHappier\nDays"}
          </p>

          <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-14 sm:pt-20 lg:pt-28 pb-16 md:pb-20 lg:pb-44">
            <div className="avian-fade-up max-w-2xl">
              <h1 className="font-brand text-5xl sm:text-6xl lg:text-[5.25rem] leading-[0.98] text-[#0a1f4d]">
                Rent a car.<br />
                <span className="text-[#1d7ffd]">Go further.</span>
              </h1>
              <p className="mt-6 text-lg sm:text-2xl text-[#475569]" data-testid="hero-subtitle">
                Simple. Flexible. For every journey.
              </p>
              <Link to="/vehicles" className="btn-primary mt-8 text-base sm:text-lg !px-9 !py-4" data-testid="hero-find-car-btn">
                Find your car <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 -mt-10 md:-mt-12 lg:-mt-28">
          <SearchBar />
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-14" data-testid="value-props">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: <ThumbsUp size={26} />, title: "Wide selection", body: "From city cars to SUVs." },
            { icon: <ShieldCheck size={26} />, title: "Transparent pricing", body: "No hidden fees." },
            { icon: <CalendarDays size={26} />, title: "Flexible booking", body: "Plans can change." },
            { icon: <Headphones size={26} />, title: "We're here for you", body: "24/7 customer support." },
          ].map((f) => (
            <div key={f.title} className="flex items-center gap-4">
              <span className="w-[76px] h-[76px] flex-shrink-0 rounded-full bg-[#F4F8FF] text-[#1d7ffd] flex items-center justify-center">
                {f.icon}
              </span>
              <div>
                <p className="font-display text-lg font-bold text-[#0a1f4d]">{f.title}</p>
                <p className="text-[#64748B] mt-0.5">{f.body}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Cities */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-24">
        <div className="flex items-end justify-between mb-5">
          <h2 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d]">Browse by city</h2>
          <Link to="/vehicles" className="text-sm font-semibold text-[#1d7ffd] hover:underline">View all</Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {(cities.length ? cities : ["Cape Town", "Johannesburg", "Durban", "Pretoria"]).map((c) => (
            <Link
              key={c}
              to={`/vehicles?city=${encodeURIComponent(c)}`}
              className="group relative aspect-[4/3] rounded-2xl overflow-hidden bg-[#08153a]"
              data-testid={`city-tile-${c}`}
            >
              <img
                src={cityImage(c)}
                alt={c}
                className="absolute inset-0 w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#08153a]/90 via-[#08153a]/30 to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-[10px] tracking-[0.18em] uppercase text-white/80 font-bold mb-1.5">South Africa</p>
                  <p className="font-display text-lg sm:text-xl lg:text-2xl font-black !text-white leading-tight break-words">{c}</p>
                </div>
                <span className="w-8 h-8 flex-shrink-0 rounded-full bg-white/15 backdrop-blur flex items-center justify-center text-white">
                  <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B] mb-2">Hand-picked rides</p>
            <h2 className="font-display text-3xl sm:text-4xl tracking-tight font-bold text-[#08153a]">Featured cars near you</h2>
          </div>
          <Link to="/vehicles" className="text-sm font-semibold text-[#1d7ffd] hover:underline">See more →</Link>
        </div>
        <div className="flex sm:grid overflow-x-auto sm:overflow-visible snap-row no-scrollbar -mx-6 px-6 sm:mx-0 sm:px-0 gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 pb-2 sm:pb-0" data-testid="featured-grid">
          {featured.map((v) => (
            <div key={v.id} className="w-[78vw] max-w-[320px] flex-shrink-0 sm:w-auto sm:max-w-none">
              <VehicleCard v={v} />
            </div>
          ))}
        </div>
        {featured.length > 1 && <p className="sm:hidden text-xs text-[#64748B] mt-2 text-center">Swipe to see more →</p>}
      </section>

      {/* How it works */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B] mb-3">How tadada works</p>
            <h2 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d] leading-tight">
              A simpler way to rent. A safer way to host.
            </h2>
            <p className="mt-4 text-[#334155] leading-relaxed">
              No queues, no paperwork, no surprises. Book in minutes and meet your host — or have the car delivered to your door.
            </p>
            <Link to="/how-it-works" className="btn-outline mt-6 inline-flex text-sm" data-testid="how-it-works-cta">Learn more</Link>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {[
              { icon: <CarFront size={22} />, title: "Find the perfect car", body: "Filter by city, brand, transmission or budget. Self-drive or with a chauffeur." },
              { icon: <CreditCard size={22} />, title: "Book & pay securely", body: "Confirmed in minutes. Refundable deposit protects everyone. Card or EFT." },
              { icon: <KeyRound size={22} />, title: "Collect & drive", body: "Meet your host or schedule delivery. Snap before-photos and hit the road." },
            ].map((s, i) => (
              <div key={i} className="rounded-2xl border border-[#E2E8F0] p-6 bg-white">
                <div className="w-11 h-11 rounded-xl bg-[#08153a] text-white flex items-center justify-center mb-4">
                  {s.icon}
                </div>
                <h3 className="font-display text-lg font-bold text-[#08153a]">{s.title}</h3>
                <p className="text-sm text-[#334155] mt-2 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust band */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-16">
        <div className="rounded-[2rem] bg-[#0a1f4d] text-white overflow-hidden relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-8 lg:p-12 items-center">
            <div className="lg:col-span-7">
              <p className="text-xs tracking-[0.18em] uppercase font-bold text-white/60 mb-3">Trust & safety</p>
              <h2 className="font-brand text-3xl sm:text-4xl">
                Every car is inspected.<br />Every booking is insured.
              </h2>
              <p className="mt-4 text-white/80 leading-relaxed max-w-xl">
                tadada verifies host identity, vehicle ownership, licence discs and roadworthy certificates. Each booking includes insurance and a digital rental agreement.
              </p>
              <div className="mt-7 flex flex-wrap gap-2.5">
                {["ID verified", "Roadworthy", "Insurance attached", "Inspection certified", "Digital agreement"].map((t) => (
                  <span key={t} className="inline-flex items-center gap-1.5 bg-white/10 border border-white/15 text-white text-xs font-semibold px-3 py-1.5 rounded-full">
                    <ShieldCheck size={12} className="text-[#1d7ffd]" /> {t}
                  </span>
                ))}
              </div>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/60">Verified hosts</p>
                  <p className="font-display text-3xl font-black mt-2">100%</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/60">Insurance cover</p>
                  <p className="font-display text-3xl font-black mt-2">R5m</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/60">Avg booking</p>
                  <p className="font-display text-3xl font-black mt-2">3 min</p>
                </div>
                <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
                  <p className="text-[10px] tracking-[0.18em] uppercase font-bold text-white/60">Cities</p>
                  <p className="font-display text-3xl font-black mt-2">4+</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Become a host */}
      <section className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-16 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
        <div className="rounded-[2rem] overflow-hidden aspect-[4/3]">
          <img src="https://images.pexels.com/photos/31064777/pexels-photo-31064777.png?auto=compress&cs=tinysrgb&dpr=2&h=720&w=960" alt="Become a host" className="w-full h-full object-cover" />
        </div>
        <div>
          <p className="text-xs tracking-[0.18em] uppercase font-bold text-[#64748B] mb-3">Earn with your car</p>
          <h2 className="font-brand text-3xl sm:text-4xl text-[#0a1f4d] leading-tight">
            Put your car to work.<br /> Make up to R12,500 / month.
          </h2>
          <p className="mt-4 text-[#334155] leading-relaxed">
            List in minutes. Set your own price and calendar. We handle bookings, payments and insurance — you keep the keys.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/host" className="btn-primary text-sm" data-testid="host-cta-primary">List your car</Link>
            <Link to="/how-it-works" className="btn-outline text-sm">How payouts work</Link>
          </div>
          <div className="mt-8 flex items-center gap-6 text-xs text-[#64748B]">
            <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-[#1d7ffd]" /> 5-min listing</span>
            <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#1d7ffd]" /> National coverage</span>
          </div>
        </div>
      </section>
    </div>
  );
}

function cityImage(c) {
  const map = {
    "Cape Town": "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&w=900&q=80",
    "Johannesburg": "https://images.unsplash.com/photo-1577948000111-9c970dfe3743?auto=format&fit=crop&w=900&q=80",
    "Durban": "https://images.pexels.com/photos/14169042/pexels-photo-14169042.jpeg?auto=compress&cs=tinysrgb&w=900",
    "Pretoria": "https://images.unsplash.com/photo-1605649487212-47bdab064df7?auto=format&fit=crop&w=900&q=80",
  };
  return map[c] || "https://images.unsplash.com/photo-1580060839134-75a5edca2e99?auto=format&fit=crop&w=900&q=80";
}
